import { Link } from "@/lib/router-compat";
import { ArrowLeft, ArrowRight, Calendar, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import SEO from "@/components/SEO";
import FadeIn from "@/components/FadeIn";
import PricingEstimator from "@/components/PricingEstimator";
import PageHero from "@/components/PageHero";
import { sanitizeArticleHtml } from "@/lib/sanitizeHtml";
import type { CmsArticle } from "@/lib/cmsArticles.functions";
import SeoRelatedLinks from "@/components/SeoRelatedLinks";
import { articleServiceLinks } from "@/lib/seoInternalLinks";
import { articleAuthorSchema } from "@/lib/articleSchema";

const SITE = "https://modernfenceinstall.com";

/**
 * Renders a CMS-authored Learn article through the same visual system as the
 * hand-built articles. Body HTML is sanitized again at render time.
 */
const CmsArticlePage = ({ article, preview = false }: { article: CmsArticle; preview?: boolean }) => {
  const canonical = `${SITE}/learn/${article.slug}`;
  const published = article.published_at ?? article.created_at;
  const bodyHtml = sanitizeArticleHtml(article.body_html);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.meta_description || article.excerpt,
    ...(article.featured_image_url ? { image: article.featured_image_url } : {}),
    datePublished: published,
    dateModified: article.updated_at,
    author: articleAuthorSchema(article.author_name),
    publisher: { "@id": `${SITE}/#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Learn", item: `${SITE}/learn` },
      { "@type": "ListItem", position: 3, name: article.title, item: canonical },
    ],
  };

  return (
    <>
      <SEO
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt}
        canonical={canonical}
        ogType="article"
        noindex={preview || article.status !== "published"}
        {...(article.og_image_url ? { ogImage: article.og_image_url } : {})}
        {...(article.og_title?.trim() ? { ogTitle: article.og_title } : {})}
        {...(article.og_description?.trim() ? { ogDescription: article.og_description } : {})}
        {...(preview ? {} : { jsonLd: [articleJsonLd, breadcrumbJsonLd] })}
      />

      <PageHero
        media="image"
        imageSrc={article.featured_image_url ?? "/logo.png"}
        imageAlt={article.featured_image_alt || article.title}
        align="left"
        overlay="strong"
        eyebrow={article.category}
        title={article.title}
        subtitle={article.excerpt}
        topSlot={
          <Link
            to="/learn"
            className="inline-flex items-center gap-2 text-mesh hover:text-charcoal-foreground text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
        }
      >
        <div className="flex flex-wrap items-center gap-4 text-sm text-mesh">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            Updated{" "}
            {new Date(article.updated_at).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </div>
      </PageHero>

      <div className="bg-offwhite border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb className="min-w-0 overflow-hidden">
            <BreadcrumbList className="min-w-0 flex-nowrap">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/learn">Learn</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="min-w-0 flex-1">
                <BreadcrumbPage className="block min-w-0 truncate">{article.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <section className="py-12 lg:py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_320px] gap-10 max-w-6xl mx-auto">
            <article
              className="cms-article min-w-0 w-full max-w-full"
              // Sanitized on write (server) and again here before rendering.
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />

            <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
              <SeoRelatedLinks heading="Related fence services" links={articleServiceLinks(article.title, article.category)} />
              <div className="bg-primary/5 rounded-xl border border-primary/20 p-6">
                <p className="font-semibold text-foreground mb-2">Ready to talk?</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Get a tailored estimate from our team — free, no obligation.
                </p>
                <Button asChild className="w-full gap-2">
                  <Link to="/book">
                    Book free consultation <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>

              <div className="bg-offwhite rounded-xl border border-border p-5">
                <p className="font-semibold text-foreground mb-2">Prefer to talk?</p>
                <a
                  href="tel:+18884163144"
                  className="inline-flex items-center gap-2 text-primary font-semibold"
                >
                  <Phone className="w-4 h-4" /> (888) 416-3144
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {!preview && (
        <FadeIn>
          <PricingEstimator />
        </FadeIn>
      )}
    </>
  );
};

export default CmsArticlePage;
